import React, { useEffect, useState } from "react";
import Header from "@user/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { IndianRupee, MessageSquare, CheckCircle2, Clock, Activity, Wallet } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  pending: "bg-amber-50 text-amber-800",
  confirmed: "bg-blue-50 text-blue-800",
  accepted: "bg-blue-50 text-blue-800",
  completed: "bg-emerald-50 text-emerald-800",
  rejected: "bg-red-50 text-red-800",
};

export default function LawyerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ total: 0, paid: 0, completed: 0, earnings: 0 });
  const [consults, setConsults] = useState([]);
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    if (!loading && (!user || user.role !== "lawyer")) navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const [p, s, c] = await Promise.all([
        api.get("/lawyer/me"),
        api.get("/lawyer/stats"),
        api.get("/lawyer/consultations"),
      ]);
      setProfile(p.data);
      setStats(s.data);
      setConsults(c.data);
      setAvailable(p.data?.available);
    } catch (e) {
      console.error("[lawyer-dashboard] failed to load profile/stats/consultations", e);
    }
  };

  useEffect(() => { if (user?.role === "lawyer") load(); /* eslint-disable-next-line */ }, [user]);

  const toggleAvailable = async (v) => {
    setAvailable(v);
    try {
      await api.post("/lawyer/availability", { available: v });
      toast.success(v ? "You are now online" : "You are offline");
    } catch { toast.error("Failed"); }
  };

  if (loading || !user || user.role !== "lawyer") {
    return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#0A2540] animate-spin" /></div>;
  }

  const incoming = consults.filter((c) => c.payment_status === "paid" && c.status !== "completed");
  const history = consults.filter((c) => c.status === "completed");

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl" />
            ) : (
              <div className="h-14 w-14 rounded-2xl adv-navy text-[#D4AF37] grid place-items-center font-bold text-xl">{user.name[0]}</div>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                {profile?.name || user.name}
              </h1>
              <p className="text-slate-500 text-sm">{profile?.specialization} · {profile?.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3">
            <Switch id="avail" checked={available} onCheckedChange={toggleAvailable} data-testid="lawyer-availability-toggle" />
            <div>
              <p className="text-sm font-bold text-[#0A2540]">{available ? "Online" : "Offline"}</p>
              <p className="text-[11px] text-slate-500">Toggle to accept bookings</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat icon={Wallet} label="Earnings" value={`₹${stats.earnings}`} />
          <Stat icon={CheckCircle2} label="Paid bookings" value={stats.paid} />
          <Stat icon={Activity} label="Total consults" value={stats.total} />
          <Stat icon={Clock} label="Completed" value={stats.completed} />
        </div>

        <Tabs defaultValue="incoming" className="mt-8">
          <TabsList data-testid="lawyer-tabs">
            <TabsTrigger value="incoming" data-testid="tab-incoming">Incoming ({incoming.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Completed ({history.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="incoming" className="mt-4">
            <ConsultList consults={incoming} emptyText="No active bookings. Toggle availability to appear in search." />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <ConsultList consults={history} emptyText="No past consultations yet." />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{value}</p>
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
    </div>
  </div>
);

const ConsultList = ({ consults, emptyText }) => (
  <div className="bg-white border border-slate-200 rounded-2xl divide-y">
    {consults.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">{emptyText}</div>}
    {consults.map((c) => (
      <Link key={c.id} to={`/consultations/${c.id}`} className="p-4 flex items-center gap-3 hover:bg-slate-50" data-testid={`lawyer-consult-${c.id}`}>
        <div className="h-10 w-10 rounded-xl bg-[#EAF1F8] text-[#0A2540] grid place-items-center">
          <MessageSquare size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0A2540] truncate">{c.user_name}</p>
          <p className="text-xs text-slate-500 truncate capitalize">{c.consultation_type} · {c.query}</p>
        </div>
        <div className="text-right">
          <Badge className={`${statusColor[c.status]} hover:${statusColor[c.status]} rounded-full capitalize`}>{c.status}</Badge>
          <p className="text-[11px] text-slate-500 mt-1 font-bold">₹{c.amount}</p>
        </div>
      </Link>
    ))}
  </div>
);
